/**
 * SelectActionsSeattlePhase - Seattle Metro Solo アクション選択フェーズUIコンポーネント
 *
 * Seattle固有ルール:
 * - 2種類のアクション（ENGINEER, LOCOMOTIVE）
 * - 1ターンに0〜2個選択可能（両方選択も可）
 * - 手数料は選択時ではなく清算時に$5×使用回数
 * - Locomotive は効果選択が必要（機関車+1 OR 貨物輸送Lv+1）
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_03_select_actions_seattle.md
 */

import { useState } from 'react';
import type { SeattleGameState, SeattleActionType, SeattleLocomotiveEffect } from '../../types';
import { useTranslations } from '../../i18n';

export interface SelectActionsSeattlePhaseProps {
  G: SeattleGameState;
  isMyTurn: boolean;
  onSelectAction: (action: SeattleActionType, locomotiveEffect?: SeattleLocomotiveEffect) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Phase 3: アクション選択フェーズUI（Seattle専用）
 */
export function SelectActionsSeattlePhase({
  G,
  isMyTurn,
  onSelectAction,
  onConfirm,
  onCancel,
}: SelectActionsSeattlePhaseProps) {
  const t = useTranslations();
  const player = G.players['0'];

  // Locomotive 効果選択モーダルの状態
  const [showLocoEffectModal, setShowLocoEffectModal] = useState(false);

  // 今ターン選択済みのアクション
  const selectedThisTurn = player.actionsSelectedThisTurn || [];
  const engineerSelected = selectedThisTurn.includes('ENGINEER');
  const locomotiveSelected = selectedThisTurn.includes('LOCOMOTIVE');

  // 各レベルの最大値チェック
  const locoAtMax = player.locomotiveLevel >= 6;
  const cargoAtMax = G.cargoTransportLevel >= 5;

  // 清算時の総コスト予測
  const totalUsage = player.engineerUsageCount + player.locomotiveUsageCount;
  const estimatedSettlementCost = totalUsage * 5;

  // Engineer クリック
  const handleEngineerClick = () => {
    if (engineerSelected) return; // 既に選択済み
    onSelectAction('ENGINEER');
  };

  // Locomotive クリック → 効果選択モーダル表示
  const handleLocomotiveClick = () => {
    if (locomotiveSelected) return; // 既に選択済み
    // 両方とも最大値の場合は選択不可
    if (locoAtMax && cargoAtMax) return;
    setShowLocoEffectModal(true);
  };

  // Locomotive 効果選択
  const handleSelectLocoEffect = (effect: SeattleLocomotiveEffect) => {
    setShowLocoEffectModal(false);
    onSelectAction('LOCOMOTIVE', effect);
  };

  return (
    <div className="space-y-4">
      {!isMyTurn ? (
        <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
      ) : (
        <>
          {/* アクション選択ボタン */}
          <div className="grid grid-cols-2 gap-2">
            {/* Engineer */}
            <button
              className={`action-card text-left ${engineerSelected ? 'border-primary bg-primary/10' : ''}`}
              onClick={handleEngineerClick}
              disabled={engineerSelected}
              data-action="ENGINEER"
              data-selected={engineerSelected}
            >
              <div className="font-heading text-sm font-semibold text-foreground leading-tight">
                {t.selectActions.actions.engineer.name}
              </div>
              <div className="text-[10px] text-muted-foreground leading-tight">
                {t.selectActions.actions.engineer.description}
              </div>
              {engineerSelected && (
                <div className="text-xs text-primary mt-1">Selected</div>
              )}
            </button>

            {/* Locomotive */}
            <button
              className={`action-card text-left ${locomotiveSelected ? 'border-primary bg-primary/10' : ''}`}
              onClick={handleLocomotiveClick}
              disabled={locomotiveSelected || (locoAtMax && cargoAtMax)}
              data-action="LOCOMOTIVE"
              data-selected={locomotiveSelected}
            >
              <div className="font-heading text-sm font-semibold text-foreground leading-tight">
                {t.selectActions.actions.locomotive.name}
              </div>
              <div className="text-[10px] text-muted-foreground leading-tight">
                {t.selectActions.seattle?.locomotiveDescription || 'Loco +1 or Cargo +1'}
              </div>
              {locomotiveSelected && (
                <div className="text-xs text-primary mt-1">Selected</div>
              )}
              {!locomotiveSelected && locoAtMax && cargoAtMax && (
                <div className="text-xs text-destructive mt-1">Both at max</div>
              )}
            </button>
          </div>

          {/* 確定/キャンセルボタン */}
          <div className="flex gap-2">
            <button
              className="btn-primary flex-1 py-2"
              onClick={onConfirm}
            >
              {selectedThisTurn.length > 0 ? t.common.confirm : t.common.pass}
            </button>
            {selectedThisTurn.length > 0 && (
              <button
                className="btn-secondary flex-1 py-2"
                onClick={onCancel}
              >
                {t.common.cancel}
              </button>
            )}
          </div>

          {/* 使用回数と清算コスト */}
          <div className="text-xs text-muted-foreground border-t pt-2 space-y-1">
            <div className="flex justify-between">
              <span>{t.selectActions.seattle?.engineerUses || 'Engineer uses'}:</span>
              <span>{player.engineerUsageCount}</span>
            </div>
            <div className="flex justify-between">
              <span>{t.selectActions.seattle?.locomotiveUses || 'Locomotive uses'}:</span>
              <span>{player.locomotiveUsageCount}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>{t.selectActions.seattle?.settlementCost || 'Settlement cost'}:</span>
              <span className="text-warning">${estimatedSettlementCost}</span>
            </div>
          </div>

          {/* ヘルプテキスト */}
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">{t.selectActions.helpTitle}</summary>
            <ul className="mt-2 ml-4 list-disc space-y-1">
              <li>{t.selectActions.seattle?.helpOptional || 'Select 0-2 actions per turn'}</li>
              <li>{t.selectActions.seattle?.helpCost || 'No immediate cost; $5/use at settlement'}</li>
              <li>{t.selectActions.seattle?.helpLocomotive || 'Locomotive: choose Loco +1 or Cargo +1'}</li>
            </ul>
          </details>

          {/* Locomotive 効果選択モーダル */}
          {showLocoEffectModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-background border rounded-lg p-4 max-w-sm w-full mx-4 space-y-4">
                <h3 className="font-heading font-semibold text-lg">
                  {t.selectActions.seattle?.locoEffectTitle || 'Choose Locomotive Effect'}
                </h3>

                <div className="space-y-2">
                  <button
                    className="w-full action-card text-left"
                    onClick={() => handleSelectLocoEffect('LOCO_UP')}
                    disabled={locoAtMax}
                  >
                    <div className="font-heading text-sm font-semibold">
                      {t.selectActions.seattle?.locoUp || 'Locomotive +1'}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Current: {player.locomotiveLevel}/6
                      {locoAtMax && ' (MAX)'}
                    </div>
                  </button>

                  <button
                    className="w-full action-card text-left"
                    onClick={() => handleSelectLocoEffect('CARGO_UP')}
                    disabled={cargoAtMax}
                  >
                    <div className="font-heading text-sm font-semibold">
                      {t.selectActions.seattle?.cargoUp || 'Cargo Transport +1'}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Current: {G.cargoTransportLevel}/5
                      {cargoAtMax && ' (MAX)'}
                    </div>
                  </button>
                </div>

                <button
                  className="btn-secondary w-full py-2"
                  onClick={() => setShowLocoEffectModal(false)}
                >
                  {t.common.cancel}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SelectActionsSeattlePhase;
